import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { formatDate } from '@/lib/dateFormatter';
import { SMS_STATUS, SmsLog } from '@/services/smsLogs/smsLogs.types';
import type { ColumnDef } from '@tanstack/react-table';

export function getColumns(t: (key: string) => string): ColumnDef<SmsLog>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-primary">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'phoneNumber',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.phoneNumber')} />
      ),
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('phoneNumber')}</div>,
      meta: {
        label: 'Phone Number',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'message',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.message')} />,
      cell: ({ row }) => (
        <div className="w-[30rem] font-medium text-caption text-primary">
          <iframe
            title="email-iframe"
            ref={(iframe) => {
              if (!iframe) return;

              iframe.onload = () => {
                try {
                  const doc = iframe.contentDocument || iframe.contentWindow?.document;
                  if (!doc) return;

                  const height = doc.body.scrollHeight;
                  iframe.style.height = height + 'px';
                } catch {
                  // Cross-origin iframe content cannot always be measured.
                }
              };
            }}
            srcDoc={`
    <html>
      <head>
        <style>
          body { margin: 0; padding: 0; overflow: hidden; font-size: 12px; }
        </style>
      </head>
      <body>
        ${String(row.getValue('message'))}
      </body>
    </html>
  `}
            style={{ width: '100%', border: 'none' }}
          />
        </div>
      ),
      meta: {
        label: 'Message',
      },
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdOn',
      enableColumnFilter: true,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdOn')} />,
      cell: ({ row }) => {
        const raw: number = row.getValue('createdDate');
        return <div className="font-medium text-caption text-primary whitespace-nowrap">{formatDate(Number(raw))}</div>;
      },
      meta: {
        label: 'Created On',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => {
        const status = row.getValue('status');
        return (
          <div className={`font-medium text-caption ${status === SMS_STATUS.SENT ? 'text-success' : 'text-warning'}`}>
            {status === SMS_STATUS.SENT ? t('common.page.table.sent') : t('common.page.table.pending')}
          </div>
        );
      },
      meta: {
        label: 'Status',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'nickname',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.nickName')} />,
      cell: ({ row }) => (
        <div className="font-medium text-caption text-primary min-w-40 break-all">{row.getValue('nickname')}</div>
      ),
      meta: {
        label: 'Nickname',
      },
      enableHiding: false,
    },
  ];
}
